/**
 * Подбор торцевых подключений (источник) по скорости 0.3–1.5 м/с
 * и правилам collectorUtils (isEndFittingCompatibleWithMainPipe / квадратный профиль).
 * Скорость для муфт считается по наружному ⌀ ряда как у ниппеля (ответный ниппель в муфте).
 */

import {
  isEndFittingCompatibleWithMainPipe,
  isDistributionEndFittingCompatibleWithSquareProfile,
  COLLECTOR_CHARACTERISTICS_MAX_SQUARE_PROFILE_VALUE,
} from './collectorUtils';

/** Категории как в DistributionCollectorEditor (подписи в UI) */
export const COLLECTOR_END_CONNECTION_CATEGORIES = [
  { id: 'Ниппели', connectionType: 'nipple' },
  { id: 'Муфты', connectionType: 'coupling' },
  { id: 'Фланцы', connectionType: 'flange' },
  { id: 'Накидные гайки', connectionType: 'nut' },
  { id: 'Шайбы с наружной резьбой', connectionType: 'washer' },
];

/** Перебор типов при автоподборе категории «Подключение источника», если для текущей нет размеров в 0,3–1,5 м/с (как у комбинированного узла). */
export const COLLECTOR_END_CONNECTION_TYPE_AUTO_FALLBACK_ORDER = [
  'flange',
  'coupling',
  'nipple',
  'nut',
  'washer',
];

export function categoryLabelToConnectionType(categoryLabel) {
  const row = COLLECTOR_END_CONNECTION_CATEGORIES.find((c) => c.id === categoryLabel);
  return row ? row.connectionType : 'nipple';
}

/** Каталог размеров (как в CharacteristicsCalculator) */
export const CONNECTION_SIZE_CATALOG = [
  { diameter: 13.7, displayName: '1/4"', nominalDiameter: null },
  { diameter: 17.2, displayName: '3/8"', nominalDiameter: null },
  { diameter: 21.3, displayName: '1/2"', nominalDiameter: null },
  { diameter: 26.9, displayName: '3/4"', nominalDiameter: null },
  { diameter: 33.7, displayName: '1"', nominalDiameter: null },
  { diameter: 42.4, displayName: '1 1/4"', nominalDiameter: null },
  { diameter: 48.3, displayName: '1 1/2"', nominalDiameter: null },
  { diameter: 60.3, displayName: '2"', nominalDiameter: null },
  { diameter: 76.1, displayName: '2 1/2"', nominalDiameter: null },
  { diameter: 88.9, displayName: '3"', nominalDiameter: null },
  { diameter: 114.3, displayName: '4"', nominalDiameter: null },
  { diameter: 25, displayName: 'Ду25', nominalDiameter: 25, isFlangeSize: true },
  { diameter: 32, displayName: 'Ду32', nominalDiameter: 32, isFlangeSize: true },
  { diameter: 40, displayName: 'Ду40', nominalDiameter: 40, isFlangeSize: true },
  { diameter: 50, displayName: 'Ду50', nominalDiameter: 50, isFlangeSize: true },
  { diameter: 65, displayName: 'Ду65', nominalDiameter: 65, isFlangeSize: true },
  { diameter: 80, displayName: 'Ду80', nominalDiameter: 80, isFlangeSize: true },
  { diameter: 100, displayName: 'Ду100', nominalDiameter: 100, isFlangeSize: true },
  { diameter: 125, displayName: 'Ду125', nominalDiameter: 125, isFlangeSize: true },
  { diameter: 150, displayName: 'Ду150', nominalDiameter: 150, isFlangeSize: true },
  { diameter: 200, displayName: 'Ду200', nominalDiameter: 200, isFlangeSize: true },
  { diameter: 250, displayName: 'Ду250', nominalDiameter: 250, isFlangeSize: true },
  { diameter: 300, displayName: 'Ду300', nominalDiameter: 300, isFlangeSize: true },
  { diameter: 350, displayName: 'Ду350', nominalDiameter: 350, isFlangeSize: true },
];

export function getTransitionPipeDiameter(connectionType, nominalDiameter) {
  if (connectionType === 'nipple') {
    return nominalDiameter;
  }
  if (connectionType === 'coupling') {
    const MUFTA_OUTER_DIAMETER = {
      13.7: 17.4,
      17.2: 20.8,
      21.3: 24.5,
      26.9: 29.9,
      33.7: 38,
      42.4: 48,
      48.3: 53.9,
      60.3: 66,
    };
    return MUFTA_OUTER_DIAMETER[nominalDiameter] || nominalDiameter;
  }
  if (connectionType === 'nut') {
    const NAK_GAIKA_TO_PIPE_DIAMETER = {
      33.7: 21.3,
      42.4: 32,
      48.3: 32,
      60.3: 42.4,
    };
    return NAK_GAIKA_TO_PIPE_DIAMETER[nominalDiameter] || nominalDiameter;
  }
  if (connectionType === 'washer') {
    const SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER = {
      33.7: 26.9,
      42.4: 32,
      48.3: 32,
      60.3: 42.4,
    };
    return SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[nominalDiameter] || nominalDiameter;
  }
  if (connectionType === 'flange') {
    const DN_TO_PIPE_DIAMETER = {
      25: 33.7,
      32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
      80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
      200: 219.1, 250: 273, 300: 323.9, 350: 355.6,
    };
    return DN_TO_PIPE_DIAMETER[nominalDiameter] || nominalDiameter;
  }
  return nominalDiameter;
}

/**
 * Наружный ⌀ для расчёта скорости в торцевом подключении (0,3–1,5 м/с).
 * Муфта: как у ниппеля того же ряда — в муфту вворачивается ответный ниппель, гидравлически сечение как у трубы ряда.
 */
export function getVelocityEffectiveOuterDiameter(connectionType, sizeData) {
  const { diameter, isFlangeSize, nominalDiameter } = sizeData;
  if (connectionType === 'flange' && isFlangeSize) {
    return getTransitionPipeDiameter('flange', nominalDiameter);
  }
  if (connectionType === 'coupling') {
    return getTransitionPipeDiameter('nipple', diameter);
  }
  return getTransitionPipeDiameter(connectionType, diameter);
}

/**
 * Межосевое между подающим и обратным коллектором (мм) по размеру торцевого подключения.
 * До 1" вкл. → 125; 1¼" → 150; 1½" → 200; 2" ниппель/муфта/гайка/шайба → 200; 2" фланец (Ду50) → 300;
 * 2½" и 3" → 300; 4" → 480.
 * Исключение: фланец Ду100 при тепловой мощности > 800 кВт и ≤ 1000 кВт → 535.
 * Крупные фланцы (номинал Ду = sizeKey): Ду125 → 640; Ду150 → 805; Ду200 → 1005; Ду250 и Ду350 → 1210.
 * Ниппели / муфты / накидные гайки / шайбы: по номиналу ряда (поле diameter каталога).
 * Прочие фланцы: по наружному ⌀ приварной трубы для выбранного Ду (как getTransitionPipeDiameter).
 *
 * @param {object} [options]
 * @param {number} [options.thermalPowerKw] — расчётная тепловая мощность, кВт (для исключений)
 */
export function getPipeSpacingBetweenSupplyReturnMm(connectionType, sizeKey, options = {}) {
  const { thermalPowerKw } = options;
  if (sizeKey == null || Number.isNaN(Number(sizeKey))) return 200;

  const pKw = thermalPowerKw == null ? NaN : Number(thermalPowerKw);
  if (
    connectionType === 'flange' &&
    Number(sizeKey) === 100 &&
    !Number.isNaN(pKw) &&
    pKw > 800 &&
    pKw <= 1000
  ) {
    return 535;
  }

  if (connectionType === 'flange') {
    const dn = Number(sizeKey);
    if (dn === 125) return 640;
    if (dn === 150) return 805;
    if (dn === 200) return 1005;
    if (dn === 250 || dn === 350) return 1210;
  }

  const refPipeMm =
    connectionType === 'flange'
      ? getTransitionPipeDiameter('flange', sizeKey)
      : sizeKey;
  const d = Number(refPipeMm);
  if (d <= 33.7) return 125;
  if (d <= 42.4) return 150;
  if (d <= 48.3) return 200;
  if (d <= 60.3) {
    return connectionType === 'flange' ? 300 : 200;
  }
  if (d <= 88.9) return 300;
  if (d <= 114.3) return 480;
  return 480;
}

export function getConnectionWallThickness(connectionType) {
  switch (connectionType) {
    case 'nipple':
    case 'coupling':
      return 3;
    case 'flange':
    case 'nut':
    case 'washer':
      return 2;
    default:
      return 2;
  }
}

export function getFittingIdFromConnection(connectionType, connectionSize) {
  if (!connectionSize) return 'Nippel_1';

  const sizeToSuffix = {
    13.7: '1.4',
    17.2: '3.8',
    21.3: '1.2',
    26.9: '3.4',
    33.7: '1',
    42.4: '1_1.4',
    48.3: '1_1.2',
    60.3: '2',
    76.1: '2_1/2',
    88.9: '3',
    114.3: '4',
  };

  if (connectionType === 'flange') {
    return `Flanec_${connectionSize}_galv`;
  }

  const suffix = sizeToSuffix[connectionSize];
  if (!suffix) return 'Nippel_1';

  if (connectionType === 'nipple') return `Nippel_${suffix}`;
  if (connectionType === 'coupling') return `Mufta_${suffix}`;
  if (connectionType === 'nut') return `Nak_Gaika_${suffix}`;
  if (connectionType === 'washer') return `Shaib_Nar_Rezba_${suffix}`;

  return 'Nippel_1';
}

export function calculateVelocityInRoundPipe(flowRateM3h, outerDiameterMm, wallThicknessMm) {
  const flowRateM3s = flowRateM3h / 3600;
  const innerDiameterMm = outerDiameterMm - 2 * wallThicknessMm;
  const innerDiameterM = innerDiameterMm / 1000;
  const area = Math.PI * Math.pow(innerDiameterM / 2, 2);
  return flowRateM3s / area;
}

const MIN_V = 0.3;
const MAX_V = 1.5;

/** Верхняя граница скорости в торцевом подключении (м/с), как в filterCollectorEndConnectionSizes. */
export const COLLECTOR_END_CONNECTION_MAX_VELOCITY_MS = MAX_V;

/**
 * @param {object} p
 * @param {number} p.actualFlowM3h
 * @param {string} p.connectionType — nipple | coupling | flange | nut | washer
 * @param {boolean} p.isSquare
 * @param {number|null} p.manifoldRoundOd — наружный ⌀ гребёнки (круг)
 * @param {string|null} p.manifoldSquareProfile — ключ профиля '80x80' (квадрат)
 */
/**
 * Максимальный расход (м³/ч) через торец при скорости до COLLECTOR_END_CONNECTION_MAX_VELOCITY_MS
 * в присоединительной трубе: перебор каталога с теми же правилами совместимости, что и filterCollectorEndConnectionSizes,
 * но без проверки по текущему расходу — выбирается сочетание с наибольшей площадью сечения.
 */
export function getMaxEndConnectionFlowM3hForManifold({
  connectionType,
  isSquare,
  manifoldSquareProfile,
  manifoldRoundOd,
}) {
  const wallThickness = getConnectionWallThickness(connectionType);
  let bestQM3h = 0;

  for (const sizeData of CONNECTION_SIZE_CATALOG) {
    const { diameter, isFlangeSize, nominalDiameter } = sizeData;

    if (connectionType === 'flange' && !isFlangeSize) continue;
    if (connectionType !== 'flange' && isFlangeSize) continue;
    if (connectionType === 'coupling' && diameter > 60.3) continue;
    if (connectionType === 'nut' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) continue;
    if (connectionType === 'washer' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) continue;
    if (connectionType === 'nipple' && diameter > 114.3) continue;

    const effectiveDiameter = getVelocityEffectiveOuterDiameter(connectionType, sizeData);

    const idSizeForRule = connectionType === 'flange' && isFlangeSize ? nominalDiameter : diameter;
    const fittingId = getFittingIdFromConnection(connectionType, idSizeForRule);

    if (!isSquare && manifoldRoundOd != null) {
      if (!isEndFittingCompatibleWithMainPipe(fittingId, manifoldRoundOd)) continue;
    }
    if (isSquare && manifoldSquareProfile) {
      if (!isDistributionEndFittingCompatibleWithSquareProfile(fittingId, manifoldSquareProfile)) {
        continue;
      }
    }

    const innerDiameterMm = effectiveDiameter - 2 * wallThickness;
    if (innerDiameterMm <= 0) continue;
    const areaM2 = Math.PI * Math.pow(innerDiameterMm / 1000 / 2, 2);
    const qM3h = COLLECTOR_END_CONNECTION_MAX_VELOCITY_MS * areaM2 * 3600;
    if (qM3h > bestQM3h) bestQM3h = qM3h;
  }

  return bestQM3h;
}

/**
 * Макс. расход (м³/ч) по торцу для распределительного коллектора-квадрата: по макс. профилю 150×150
 * и наибольшему из всех типов подключений из COLLECTOR_END_CONNECTION_CATEGORIES (как в UI).
 */
export function getMaxEndConnectionFlowM3hDistributionCollectorSquareMaxProfile() {
  let bestQM3h = 0;
  for (const { connectionType } of COLLECTOR_END_CONNECTION_CATEGORIES) {
    const q = getMaxEndConnectionFlowM3hForManifold({
      connectionType,
      isSquare: true,
      manifoldSquareProfile: COLLECTOR_CHARACTERISTICS_MAX_SQUARE_PROFILE_VALUE,
      manifoldRoundOd: null,
    });
    if (q > bestQM3h) bestQM3h = q;
  }
  return bestQM3h;
}

export function filterCollectorEndConnectionSizes({
  actualFlowM3h,
  connectionType,
  isSquare,
  manifoldRoundOd,
  manifoldSquareProfile,
}) {
  const wallThickness = getConnectionWallThickness(connectionType);

  return CONNECTION_SIZE_CATALOG.filter((sizeData) => {
    const { diameter, isFlangeSize, nominalDiameter } = sizeData;

    if (connectionType === 'flange' && !isFlangeSize) return false;
    if (connectionType !== 'flange' && isFlangeSize) return false;

    if (connectionType === 'coupling' && diameter > 60.3) return false;
    if (connectionType === 'nut' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
    if (connectionType === 'washer' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
    if (connectionType === 'nipple' && diameter > 114.3) return false;

    const effectiveDiameter = getVelocityEffectiveOuterDiameter(connectionType, sizeData);

    const idSizeForRule = (connectionType === 'flange' && isFlangeSize) ? nominalDiameter : diameter;
    const fittingId = getFittingIdFromConnection(connectionType, idSizeForRule);

    if (!isSquare && manifoldRoundOd != null) {
      if (!isEndFittingCompatibleWithMainPipe(fittingId, manifoldRoundOd)) return false;
    }
    if (isSquare && manifoldSquareProfile) {
      if (!isDistributionEndFittingCompatibleWithSquareProfile(fittingId, manifoldSquareProfile)) {
        return false;
      }
    }

    const velocity = calculateVelocityInRoundPipe(actualFlowM3h, effectiveDiameter, wallThickness);
    return velocity >= MIN_V && velocity <= MAX_V;
  });
}

/** Размер = поле diameter из элемента каталога; подобрать ближайший к целевой скорости */
export function pickOptimalConnectionSizeKey(filteredSizes, connectionType, actualFlowM3h, targetVelocity = 0.9) {
  if (!filteredSizes.length) return null;
  const wallThickness = getConnectionWallThickness(connectionType);
  let best = filteredSizes[0];
  let bestDiff = Infinity;
  for (const sizeData of filteredSizes) {
    const effD = getVelocityEffectiveOuterDiameter(connectionType, sizeData);
    const v = calculateVelocityInRoundPipe(actualFlowM3h, effD, wallThickness);
    const diff = Math.abs(v - targetVelocity);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = sizeData;
    }
  }
  return best.diameter;
}

export function velocityForConnectionSize(connectionType, sizeKey, actualFlowM3h) {
  const sizeData = CONNECTION_SIZE_CATALOG.find((s) => s.diameter === sizeKey);
  if (!sizeData) return null;
  const wallThickness = getConnectionWallThickness(connectionType);
  const effD = getVelocityEffectiveOuterDiameter(connectionType, sizeData);
  return calculateVelocityInRoundPipe(actualFlowM3h, effD, wallThickness);
}
